import React from "react";
import "./PageOutline.css";

export type OutlineItem = {
  label: string;
  detail: string;
  status: "live" | "derived" | "pending" | "mock";
};

type Props = {
  whatItShows: string;
  dataSources: OutlineItem[];
  audience: string;
  updateTrigger?: string;
  notes?: string[];
};

const STATUS_LABEL: Record<OutlineItem["status"], string> = {
  live: "Live backend",
  derived: "Client-derived",
  pending: "Backend pending",
  mock: "Mock only (dev)",
};

export function PageOutline({
  whatItShows,
  dataSources,
  audience,
  updateTrigger,
  notes,
}: Props) {
  return (
    <details className="oht-outline">
      <summary className="oht-outline-head">
        <span className="oht-outline-eyebrow">About this page</span>
        <span className="oht-outline-note">Review notes — hidden before launch</span>
        <span className="oht-outline-caret" aria-hidden="true">▾</span>
      </summary>

      <div className="oht-outline-body">
        <div className="oht-outline-grid">
          <div className="oht-outline-block">
            <h4>What this page shows</h4>
            <p>{whatItShows}</p>
          </div>

          <div className="oht-outline-block">
            <h4>Who sees it</h4>
            <p>{audience}</p>
          </div>

          {updateTrigger ? (
            <div className="oht-outline-block">
              <h4>How it updates</h4>
              <p>{updateTrigger}</p>
            </div>
          ) : null}
        </div>

        <div className="oht-outline-sources">
          <h4>Data sources</h4>
          <ul>
            {dataSources.map((s, i) => (
              <li key={i}>
                <span className={`oht-outline-tag oht-outline-tag--${s.status}`}>
                  {STATUS_LABEL[s.status]}
                </span>
                <span className="oht-outline-source-label">{s.label}</span>
                <span className="oht-outline-source-detail">{s.detail}</span>
              </li>
            ))}
          </ul>
        </div>

        {notes && notes.length > 0 ? (
          <div className="oht-outline-notes">
            <h4>Notes for backend / reviewers</h4>
            <ul>
              {notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </details>
  );
}
